import { useState, useCallback, memo } from "react"
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  NativeSelect,
  Spinner,
  Stack,
  Table,
  Text,
  VStack,
  createListCollection,
} from "@chakra-ui/react"
import { useQuery } from "@apollo/client"
import { useActiveOrganization } from "../../auth/AuthProvider"
import { inviteMember, removeMember, leaveOrganization } from "../../auth/auth"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { CreateButton, UpdateButton } from "@hydrologiq/shared/content/components/Button"
import { PendingInvitations } from "../organization/PendingInvitations"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"

const ROLE_OPTIONS = createListCollection({
  items: [
    { label: "Member", value: "member" },
    { label: "Admin", value: "admin" },
    { label: "Owner", value: "owner" },
  ],
})

// Memoized dialog content for invite member
const InviteMemberDialogContent = memo<{
  inviting: boolean
  onSubmit: (data: { email: string; role: string }) => void
  onCancel: () => void
}>(({ inviting, onSubmit, onCancel }) => {
  const [email, setEmail] = useState("")
  const [role, setRole] = useState("member")

  const handleSubmit = useCallback(() => {
    if (!email || !email.includes("@")) {
      toaster.error({ title: "Please enter a valid email address" })
      return
    }
    onSubmit({ email, role })
  }, [email, role, onSubmit])

  return (
    <VStack gap={4} align="stretch">
      <Box>
        <Text fontSize="sm" mb={2}>
          Email
        </Text>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@example.com" type="email" />
      </Box>
      <Box>
        <Text fontSize="sm" mb={2}>
          Role
        </Text>
        <NativeSelect.Root>
          <NativeSelect.Field value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLE_OPTIONS.items.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </NativeSelect.Field>
        </NativeSelect.Root>
      </Box>
      <Text fontSize="sm" color="fg.muted">
        The user will receive an email invitation to join the organization.
      </Text>
      <HStack>
        <CreateButton onClick={handleSubmit} loading={inviting}>
          Send Invitation
        </CreateButton>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </VStack>
  )
})

InviteMemberDialogContent.displayName = "InviteMemberDialogContent"

// Memoized dialog content for removing member
const RemoveMemberDialogContent = memo<{
  member: any
  removing: boolean
  onSubmit: () => void
  onCancel: () => void
}>(({ member, removing, onSubmit, onCancel }) => {
  return (
    <VStack gap={4} align="stretch">
      <Text>
        Are you sure you want to remove <strong>{member?.email}</strong> from the organization?
      </Text>
      <Text fontSize="sm" color="fg.muted">
        They will lose access to all organization data and resources.
      </Text>
      <HStack>
        <Button colorPalette="red" onClick={onSubmit} loading={removing}>
          Remove Member
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </VStack>
  )
})

RemoveMemberDialogContent.displayName = "RemoveMemberDialogContent"

// Memoized dialog content for leaving organization
const LeaveOrganizationDialogContent = memo<{
  organizationName: string
  leaving: boolean
  onSubmit: () => void
  onCancel: () => void
}>(({ organizationName, leaving, onSubmit, onCancel }) => {
  return (
    <VStack gap={4} align="stretch">
      <Text>
        Are you sure you want to leave <strong>{organizationName}</strong>?
      </Text>
      <Text fontSize="sm" color="fg.muted">
        You will lose access to all organization data and resources. You'll need to be invited again to rejoin.
      </Text>
      <HStack>
        <Button colorPalette="red" onClick={onSubmit} loading={leaving}>
          Leave Organization
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </HStack>
    </VStack>
  )
})

LeaveOrganizationDialogContent.displayName = "LeaveOrganizationDialogContent"

/**
 * MembersTab - Organisation members management tab.
 *
 * Displays member list, pending invitations, invite/remove dialogs,
 * and leave organisation option. Fetches its own data independently.
 */
export function MembersTab() {
  const { activeOrganization } = useActiveOrganization()
  const [error, setError] = useState<string | null>(null)

  // Dialog states
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [showRemoveDialog, setShowRemoveDialog] = useState(false)
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)
  const [selectedMember, setSelectedMember] = useState<any>(null)

  // Action loading states
  const [inviting, setInviting] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [leaving, setLeaving] = useState(false)

  const currentUserId = activeOrganization?.member.userId
  const currentUserRole = activeOrganization?.member.role
  const isOwner = currentUserRole === "owner"

  // Load organization details using GraphQL
  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useQuery(gqlTypes.GetOrganizationDetailsDocument, {
    variables: { organizationId: activeOrganization?.organization.id || "" },
    skip: !activeOrganization?.organization.id,
    fetchPolicy: "network-only",
    onError: (err) => {
      console.error("Failed to load organisation:", err)
      setError("Failed to load organisation")
    },
  })

  const organization = data?.getOrganizationDetails
    ? {
        id: data.getOrganizationDetails.id,
        name: data.getOrganizationDetails.name,
        slug: data.getOrganizationDetails.slug,
        members: data.getOrganizationDetails.members.map((m) => ({
          id: m.id,
          organizationId: m.organizationId,
          userId: m.userId,
          email: m.email,
          role: m.role,
          createdAt: new Date(m.createdAt),
        })),
        invitations: data.getOrganizationDetails.invitations.map((i) => ({
          id: i.id,
          organizationId: i.organizationId,
          email: i.email,
          role: i.role,
          status: i.status,
          expiresAt: new Date(i.expiresAt),
          inviterId: i.inviterId,
        })),
      }
    : null

  const loadOrganization = useCallback(async () => {
    await refetch()
  }, [refetch])

  // Handle invite member
  const handleInviteMember = useCallback(
    async (data: { email: string; role: string }) => {
      if (!activeOrganization?.organization.id) return

      setInviting(true)
      try {
        await inviteMember({
          organizationId: activeOrganization.organization.id,
          email: data.email,
          role: data.role,
        })

        toaster.success({
          title: "Invitation sent",
          description: `Invitation sent to ${data.email}`,
        })

        setShowInviteDialog(false)
        await loadOrganization()
      } catch (err: any) {
        toaster.error({
          title: "Failed to send invitation",
          description: err.message || "Unknown error",
        })
      } finally {
        setInviting(false)
      }
    },
    [activeOrganization?.organization.id, loadOrganization]
  )

  // Handle remove member
  const handleRemoveMember = useCallback(async () => {
    if (!selectedMember) return

    setRemoving(true)
    try {
      await removeMember({
        organizationId: selectedMember.organizationId,
        memberIdOrEmail: selectedMember.email,
      })

      toaster.success({
        title: "Member removed",
        description: `${selectedMember.email} has been removed from the organization`,
      })

      setShowRemoveDialog(false)
      setSelectedMember(null)
      await loadOrganization()
    } catch (err: any) {
      toaster.error({
        title: "Failed to remove member",
        description: err.message || "Unknown error",
      })
    } finally {
      setRemoving(false)
    }
  }, [selectedMember, loadOrganization])

  // Handle leave organization
  const handleLeaveOrganization = useCallback(async () => {
    if (!activeOrganization?.organization.id) return

    setLeaving(true)
    try {
      await leaveOrganization({
        organizationId: activeOrganization.organization.id,
      })

      toaster.success({
        title: "Left organization",
        description: "You have left the organization",
      })

      window.location.href = "/"
    } catch (err: any) {
      toaster.error({
        title: "Failed to leave organization",
        description: err.message || "Unknown error",
      })
      setLeaving(false)
    }
  }, [activeOrganization?.organization.id])

  if (!activeOrganization?.organization.id) {
    return (
      <Stack gap={6} py={4}>
        <Text fontSize="sm" color="fg.muted">
          You don't have an active organization. Accept an invitation or create one to get started.
        </Text>
        <PendingInvitations />
      </Stack>
    )
  }

  if (loading) {
    return (
      <Box py={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Loading members...</Text>
      </Box>
    )
  }

  if (queryError || error || !organization) {
    return (
      <Stack gap={4} py={4}>
        <Text color="red.500">
          {error || queryError?.message || "Failed to load organisation members"}
        </Text>
        <PendingInvitations />
      </Stack>
    )
  }

  return (
    <Stack gap={6} py={4}>
      {/* Pending Invitations (User's incoming invites) */}
      <PendingInvitations />

      {/* Members Section */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Text fontSize="lg" fontWeight="bold">
            Members ({organization.members.length})
          </Text>
          {isOwner && (
            <CreateButton
              context={{ label: "Invite Member", description: "Invite a member to the organisation" }}
              onClick={() => setShowInviteDialog(true)}
            />
          )}
        </HStack>

        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Email</Table.ColumnHeader>
              <Table.ColumnHeader>Role</Table.ColumnHeader>
              <Table.ColumnHeader>Joined</Table.ColumnHeader>
              {isOwner && <Table.ColumnHeader>Actions</Table.ColumnHeader>}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {organization.members.map((member) => {
              const isCurrentUser = member.userId === currentUserId

              return (
                <Table.Row key={member.id}>
                  <Table.Cell>
                    <Text fontWeight={isCurrentUser ? "bold" : "normal"}>
                      {member.email}
                      {isCurrentUser && (
                        <Badge ml={2} colorPalette="blue">
                          You
                        </Badge>
                      )}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      colorPalette={member.role === "owner" ? "purple" : member.role === "admin" ? "blue" : "gray"}
                    >
                      {member.role}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm">{new Date(member.createdAt).toLocaleDateString()}</Text>
                  </Table.Cell>
                  {isOwner && (
                    <Table.Cell>
                      {!isCurrentUser && (
                        <UpdateButton
                          context={{
                            label: "Remove Member",
                            description: "Remove the member from the organisation",
                            variant: "warning",
                          }}
                          onClick={() => {
                            setSelectedMember(member)
                            setShowRemoveDialog(true)
                          }}
                        />
                      )}
                    </Table.Cell>
                  )}
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Pending Organisation Invitations */}
      {organization.invitations && organization.invitations.length > 0 && (
        <Box>
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Pending Invitations ({organization.invitations.length})
          </Text>

          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Email</Table.ColumnHeader>
                <Table.ColumnHeader>Role</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader>Expires</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {organization.invitations.map((invitation) => (
                <Table.Row key={invitation.id}>
                  <Table.Cell>
                    <Text>{invitation.email}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette="gray">{invitation.role}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette="yellow">{invitation.status}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm">{new Date(invitation.expiresAt).toLocaleDateString()}</Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}

      {/* Leave Organization */}
      {!isOwner && (
        <Box>
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            Danger Zone
          </Text>
          <UpdateButton
            context={{ label: "Leave Organisation", description: "Leave the organisation", variant: "warning" }}
            onClick={() => setShowLeaveDialog(true)}
          />
        </Box>
      )}

      {/* Invite Member Dialog */}
      <Dialog
        title="Invite New Member"
        open={showInviteDialog}
        setOpen={setShowInviteDialog}
        loading={inviting}
        disabled={inviting}
      >
        <InviteMemberDialogContent
          inviting={inviting}
          onSubmit={handleInviteMember}
          onCancel={() => setShowInviteDialog(false)}
        />
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog
        title="Remove Member"
        open={showRemoveDialog}
        setOpen={setShowRemoveDialog}
        loading={removing}
        disabled={removing}
      >
        <RemoveMemberDialogContent
          member={selectedMember}
          removing={removing}
          onSubmit={handleRemoveMember}
          onCancel={() => setShowRemoveDialog(false)}
        />
      </Dialog>

      {/* Leave Organization Dialog */}
      <Dialog
        title="Leave Organization"
        open={showLeaveDialog}
        setOpen={setShowLeaveDialog}
        loading={leaving}
        disabled={leaving}
      >
        <LeaveOrganizationDialogContent
          organizationName={organization.name}
          leaving={leaving}
          onSubmit={handleLeaveOrganization}
          onCancel={() => setShowLeaveDialog(false)}
        />
      </Dialog>
    </Stack>
  )
}
